import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import type { NodeModel } from "@/models/nodes/types";
import type { InlineEditableNode } from "@/models/nodes/visitors/NodeVisitor";
import type { UnitNodePlacement } from "@corca/data";

export function shouldEditNode(node: NodeModel & InlineEditableNode, direction: "left" | "right") {
  const { doc } = node;

  switch (node.type) {
    case "number": {
      const subSup = node.power || node.subscript;

      if (subSup && doc.cursor.node === subSup.parameters[0]) {
        if (direction === "left" && doc.cursor.index === 0) {
          return true;
        }

        if (direction === "right") {
          return false;
        }
      }

      const { placement } = node.unitNode ?? {};

      if (
        (direction === "left" && placement === "postfix" && !subSup) ||
        (direction === "right" && placement === "prefix")
      ) {
        return true;
      }

      return false;
    }

    case "function": {
      if (direction === "right") {
        return false;
      }

      if (direction === "left" && doc.cursor.index === 0) {
        if (node.power && doc.cursor.node === node.power.nodes[0]) {
          return true;
        }

        const label = node.symbolNode?.labelNodes.find(
          (n) => n.position === "right-top" || n.position === "right-bottom",
        );

        if (label?.inlineNode === doc.cursor.node) {
          return true;
        }

        if (doc.cursor.node === node.parameters[0]) {
          if (node.power || label) {
            return false;
          }

          return true;
        }
      }

      return false;
    }

    default: {
      return false;
    }
  }
}

export function getChildNode(
  node: NodeModel & InlineEditableNode,
  direction: "left" | "right",
  source: "idle" | "editing",
): [InlineNodeModel, number] | undefined {
  switch (node.type) {
    case "number": {
      if (source === "editing" && direction === "right") {
        const subSup = node.power || node.subscript;

        if (subSup) {
          const inlineNode = subSup?.parameters[0];

          if (inlineNode) {
            return [inlineNode, getIndex(inlineNode, direction)];
          }
        }
      }

      const { unitNode } = node;

      if (unitNode?.inlineNode) {
        let placement: UnitNodePlacement | undefined;

        if (source === "idle") {
          placement = direction === "left" ? "postfix" : "prefix";
        } else {
          placement = direction === "left" ? "prefix" : "postfix";
        }

        if (unitNode.placement === placement) {
          return [unitNode.inlineNode, getIndex(unitNode.inlineNode, direction)];
        }
      }

      if (source === "idle" && direction === "left") {
        const subSup = node.power || node.subscript;

        if (subSup) {
          const inlineNode = subSup?.parameters[0];

          if (inlineNode) {
            return [inlineNode, getIndex(inlineNode, direction)];
          }
        }
      }

      return undefined;
    }

    case "function": {
      if (source === "editing" && direction === "right") {
        const label = node.symbolNode?.labelNodes.find(
          (n) => n.position === "right-top" || n.position === "right-bottom",
        );

        if (label?.inlineNode) {
          return [label.inlineNode, getIndex(label.inlineNode, direction)];
        }

        if (node.power) {
          const inlineNode = node.power.nodes[0] as InlineNodeModel | undefined;

          if (inlineNode) {
            return [inlineNode, getIndex(inlineNode, direction)];
          }
        }
      }

      let paramIndex = -1;

      if (source === "idle") {
        paramIndex = direction === "right" ? -1 : node.parameters.length - 1;
      } else {
        paramIndex = direction === "right" ? 0 : -1;
      }

      const param = node.parameters[paramIndex];

      if (param) {
        return [param, getIndex(param, direction)];
      }

      return undefined;
    }

    default: {
      return undefined;
    }
  }
}

function getIndex(node: InlineNodeModel, direction: "left" | "right") {
  return direction === "left" ? node.nodes.length : 0;
}
